import {
  OreHeading,
  OreInput,
  OreMessageBar,
  OreStack,
  OreText,
  OreWysiwyg,
} from '@edenredespana/oreneta'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import { useAutoEnrollment } from '@/AutoEnrollment/ui/context'
import { Step1Form } from '@/AutoEnrollment/ui/hooks'
import { handleErrors } from '@/Shared/ui/form'

export const Step1BillingGeneralData = (): JSX.Element => {
  const { t } = useTranslation()

  const {
    cifController: { checkCif, currentCheckedCif },
  } = useAutoEnrollment()

  const formContext = useFormContext<Step1Form>()
  const { register, formState } = formContext
  const [cifBanner, setCifBanner] = useState<boolean>(false)
  const { errors } = handleErrors(formState)

  const { onChange, ...rest } = register('cif')

  const registeredCif = (cif: string): void => {
    if (cif.length === 9 && cif !== currentCheckedCif.cif) {
      checkCif(cif).then((result: boolean) => {
        if (result) {
          formContext.setError('cif', {
            type: 'manual',
            message: t(
              AutoEnrollmentTranslation.billingGeneralData.data.cif.alreadyExistsError,
            ),
          })
          setCifBanner(true)
        } else {
          setCifBanner(false)
        }
      })
    } else if (
      cif.length === 9 &&
      cif === currentCheckedCif.cif &&
      currentCheckedCif.alreadyExists
    ) {
      formContext.setError('cif', {
        type: 'manual',
        message: t(
          AutoEnrollmentTranslation.billingGeneralData.data.cif.alreadyExistsError,
        ),
      })
      setCifBanner(true)
    } else setCifBanner(false)
  }

  useEffect(() => {
    if (
      formState.errors.cif &&
      formState.errors.cif.message ===
        t(AutoEnrollmentTranslation.billingGeneralData.data.cif.alreadyExistsError)
    ) {
      setCifBanner(true)
    }
  }, [formState])

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(AutoEnrollmentTranslation.billingGeneralData.title)}
      </OreHeading>
      {cifBanner ? (
        <OreMessageBar color={'error'}>
          <OreWysiwyg>
            <OreText
              tone={'error-600'}
              dangerouslySetInnerHTML={{
                __html: t(
                  AutoEnrollmentTranslation.billingGeneralData.data.cif.warningError,
                ),
              }}
            />
          </OreWysiwyg>
        </OreMessageBar>
      ) : null}
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...rest}
          {...errors('cif')}
          id="cif"
          label={t(AutoEnrollmentTranslation.billingGeneralData.data.cif.label)}
          placeholder={t(
            AutoEnrollmentTranslation.billingGeneralData.data.cif.placeholder,
          )}
          labelTooltip={t(AutoEnrollmentTranslation.billingGeneralData.data.cif.tooltip)}
          maxLength={9}
          required
          onChange={e => {
            onChange(e)
            registeredCif(e.target.value)
          }}
        />
        <OreInput
          {...register('businessName')}
          {...errors('businessName')}
          id="businessName"
          label={t(AutoEnrollmentTranslation.billingGeneralData.data.businessName.label)}
          placeholder={t(
            AutoEnrollmentTranslation.billingGeneralData.data.businessName.placeholder,
          )}
          labelTooltip={t(
            AutoEnrollmentTranslation.billingGeneralData.data.businessName.tooltip,
          )}
          maxLength={255}
          required
        />
      </OreStack>
    </OreStack>
  )
}
